// src/pages/portal/PortalDevicesPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PortalLayout from './PortalLayout';
import { deviceService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileHero from '../../components/common/MobileHero';
import { C } from '../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────

const FONT = "'Plus Jakarta Sans', sans-serif";

const STATUS_STYLES = {
    active:  { color: C.green,  bg: C.greenLight, border: C.greenBorder },
    revoked: { color: C.red,    bg: C.redLight,   border: '#fecaca' },
    expired: { color: C.amber,  bg: C.amberLight, border: '#fde68a' },
};

const formatDate = (isoStr) => {
    if (!isoStr) return '—';
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (isoStr) => {
    if (!isoStr) return '—';
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ─── SmartphoneIcon (inline SVG) ──────────────────────────────────────────────
const SmartphoneIcon = ({ size = 28, color = C.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
);

// ─── Mobile device card ───────────────────────────────────────────────────────
const MobileDeviceCard = ({ device, onRevoke, t }) => {
    const tone = device.status === 'active' ? 'green' : device.status === 'expired' ? 'amber' : 'red';
    return (
        <MobileCard
            accent={device.status === 'revoked' ? 'red' : undefined}
            title={device.device_name || t('unknown_device')}
            status={{ label: t(`status_${device.status}`), tone }}
            subtitle={`${t('last_active')}: ${formatDateTime(device.last_used_at)}`}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                    { label: t('created'), value: formatDate(device.created_at) },
                    { label: t('last_active'), value: formatDateTime(device.last_used_at) },
                    { label: t('expires'), value: formatDate(device.expires_at) },
                    { label: t('device_id') || 'Device ID', value: device.id },
                ].map((f, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                        <span style={{ color: C.muted, fontWeight: 600 }}>{f.label}</span>
                        <span style={{ color: C.text, fontWeight: 600, textAlign: 'right', wordBreak: 'break-all' }}>{f.value}</span>
                    </div>
                ))}
                {device.status === 'active' && (
                    <button
                        onClick={() => onRevoke(device)}
                        style={{
                            marginTop: 4, width: '100%', padding: '12px 16px', borderRadius: 8,
                            border: `1px solid ${C.red}`, background: C.redLight, color: C.red,
                            fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                        }}
                    >
                        {t('reset_device')}
                    </button>
                )}
            </div>
        </MobileCard>
    );
};

const PortalDevicesPage = () => {
    const { t } = useLanguage();
    const isMobile = useIsMobile();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revoking, setRevoking] = useState(false);
    const modalBackdropRef = useRef(null);

    const fetchDevices = useCallback(async () => {
        setLoading(true);
        try {
            const data = await deviceService.listDevices();
            setDevices(data);
        } catch (err) {
            console.error('Failed to load devices:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDevices(); }, [fetchDevices]);

    const handleRevoke = async () => {
        if (!revokeTarget) return;
        setRevoking(true);
        try {
            await deviceService.revokeDevice(revokeTarget.id);
            setRevokeTarget(null);
            await fetchDevices();
        } catch (err) {
            console.error('Failed to revoke device:', err);
        } finally {
            setRevoking(false);
        }
    };

    // ─── Empty State ──────────────────────────────────────────────────────────
    const renderEmptyState = () => (
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{
                width: 64, height: 64, background: C.greenLight, borderRadius: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
            }}>
                <SmartphoneIcon size={28} color={C.green} />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: '0 0 8px', fontFamily: FONT }}>
                {t('no_devices')}
            </h3>
            <p style={{ fontSize: 13, color: C.muted, maxWidth: 360, margin: '0 auto', lineHeight: 1.6 }}>
                {t('no_devices_description')}
            </p>
        </div>
    );

    // Focus the modal backdrop when it appears so Escape works immediately
    useEffect(() => {
        if (revokeTarget && modalBackdropRef.current) {
            modalBackdropRef.current.focus();
        }
    }, [revokeTarget]);

    // ─── Confirmation Modal ───────────────────────────────────────────────────
    const renderRevokeModal = () => {
        if (!revokeTarget) return null;
        return (
            <div
                ref={modalBackdropRef}
                tabIndex={-1}
                style={{
                    position: 'fixed', inset: 0, zIndex: 1000,
                    background: 'rgba(0,0,0,0.4)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    outline: 'none',
                }}
                onClick={() => !revoking && setRevokeTarget(null)}
                onKeyDown={(e) => { if (e.key === 'Escape' && !revoking) setRevokeTarget(null); }}
            >
                <div className="portal-modal-card" style={{
                    background: C.white, borderRadius: 12, padding: 28,
                    width: 420, maxWidth: '90vw', boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                    fontFamily: FONT,
                }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <div style={{
                            width: 40, height: 40, borderRadius: 10, background: C.redLight,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.red}
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                        </div>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>
                            {t('reset_device_title')}
                        </h3>
                    </div>
                    <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: '0 0 24px' }}>
                        {t('reset_device_confirmation')}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button
                            onClick={() => setRevokeTarget(null)}
                            disabled={revoking}
                            style={{
                                padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.border}`,
                                background: C.white, color: C.text, fontSize: 13, fontWeight: 600,
                                cursor: 'pointer', fontFamily: FONT,
                            }}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            onClick={handleRevoke}
                            disabled={revoking}
                            style={{
                                padding: '8px 18px', borderRadius: 8, border: 'none',
                                background: C.red, color: C.white, fontSize: 13, fontWeight: 600,
                                cursor: revoking ? 'not-allowed' : 'pointer', fontFamily: FONT,
                                opacity: revoking ? 0.6 : 1,
                            }}
                        >
                            {revoking ? t('revoking') : t('reset_device')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ─── Main Render ──────────────────────────────────────────────────────────
    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT }}>
                {/* Header */}
                <div style={{ marginBottom: 28 }}>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: '0 0 6px' }}>
                        {t('devices')}
                    </h2>
                    <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.5 }}>
                        {t('devices_description')}
                    </p>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 60, color: C.muted, fontSize: 14 }}>
                        {t('loading')}...
                    </div>
                ) : devices.length === 0 ? (
                    renderEmptyState()
                ) : isMobile ? (
                    <>
                        <MobileHero
                            label={t('devices')}
                            value={String(devices.filter(d => d.status === 'active').length)}
                            meta={`${devices.length} ${t('registered_total') || 'registered total'}`}
                        />
                        {devices.map(device => (
                            <MobileDeviceCard key={device.id} device={device} onRevoke={setRevokeTarget} t={t} />
                        ))}
                    </>
                ) : (
                    <div style={{
                        background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
                        overflow: 'hidden',
                    }}>
                        <div className="portal-table-wrap">
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                    {[
                                        t('device_name'),
                                        t('created'),
                                        t('last_active'),
                                        t('expires'),
                                        t('status'),
                                        '',
                                    ].map((h, i) => (
                                        <th key={i} style={{
                                            padding: '12px 16px', textAlign: 'left',
                                            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                                            letterSpacing: 0.8, color: C.muted, background: C.bg,
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {devices.map((device) => {
                                    const st = STATUS_STYLES[device.status] || STATUS_STYLES.active;
                                    return (
                                        <tr
                                            key={device.id}
                                            style={{ borderBottom: `1px solid ${C.border}`, transition: 'background 0.1s' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
                                        >
                                            <td style={{ padding: '14px 16px', fontSize: 13, fontWeight: 600, color: C.text }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <SmartphoneIcon size={18} color={C.mutedLight} />
                                                    <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {device.device_name || t('unknown_device')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: C.muted }}>
                                                {formatDate(device.created_at)}
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: C.muted }}>
                                                {formatDateTime(device.last_used_at)}
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: C.muted }}>
                                                {formatDate(device.expires_at)}
                                            </td>
                                            <td style={{ padding: '14px 16px' }}>
                                                <span style={{
                                                    display: 'inline-block', fontSize: 11, fontWeight: 700,
                                                    padding: '3px 10px', borderRadius: 6,
                                                    color: st.color, background: st.bg,
                                                    textTransform: 'capitalize',
                                                }}>
                                                    {t(`status_${device.status}`)}
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                                                {device.status === 'active' && (
                                                    <button
                                                        onClick={() => setRevokeTarget(device)}
                                                        style={{
                                                            padding: '6px 14px', borderRadius: 7,
                                                            border: `1px solid ${C.red}`, background: C.white,
                                                            color: C.red, fontSize: 12, fontWeight: 600,
                                                            cursor: 'pointer', fontFamily: FONT,
                                                            transition: 'background 0.15s, color 0.15s',
                                                        }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.background = C.red; e.currentTarget.style.color = C.white; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.background = C.white; e.currentTarget.style.color = C.red; }}
                                                    >
                                                        {t('reset_device')}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
            </div>
            {renderRevokeModal()}
        </PortalLayout>
    );
};

export default PortalDevicesPage;
